// AUTO-GENERATED by backend/seeds/build_skills.py — do not edit by hand.
// First-paint fallback only; /api/skills is the source of truth.
// Skills are the HOW (voice, structure, conversion framing); the prompt is the WHAT.

export const mockSkills = [
  {
    id: "sk_instagram_post",
    name: "InstagramPost",
    description: "Make Instagram posts stop the scroll — a card-ready hook + a caption built for saves and shares.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: InstagramPost
description: Make Instagram posts stop the scroll — a card-ready hook + a caption built for saves and shares.
phase: generation
---

## When to use

Auto-applied to any post targeting **Instagram**. The hook becomes the text on the
image card, so it has to land as a standalone visual; the caption does the selling.

## Instructions

- **Hook = the card.** Write it to stand alone on a graphic: **6–10 words**, one bold
  idea, a pattern-interrupt or a claim that makes someone stop. No hashtags, no emoji
  in the hook. It must be true to the topic — never clickbait you don't pay off.
- **Caption = the payoff.** Open by extending the hook (the first ~125 chars show in
  feed before "more"), then deliver the value in **short, scannable lines** with white
  space between them. Conversational, on-brand, a little personality.
- **Earn the algorithm.** End with ONE clear CTA. Saves/shares/comments beat a
  generic ask:
  - "Save this so you've got it when you need it."
  - "Send this to someone who needs to hear it."
  - "Comment [WORD] and we'll DM you the details."
- **Sending people to a site:** Instagram captions do NOT make links clickable —
  the only tappable link on a post is the one in the profile **bio**. So when the
  goal is to drive traffic, the CTA is **"link in bio"** (the operator sets their
  bio link to the site). You MAY also write the site as plain text for reference
  (e.g. "More at yoursite.com"), but never imply that text is tappable. Use the
  brand's Website when one is set.
- **Emoji:** purposeful and sparse (a few, never one per line).
- Don't invent specifics; keep to the prompt and any sources.

## Output format

Return the usual JSON. Map it to Instagram like this:
- \`hook\`: the 6–10 word card line.
- \`body\`: the caption — short lines + white space + a one-line CTA at the end.
- \`cta\`: the single engagement CTA (save / share / comment / DM / link in bio).
- \`hashtags\`: **8–15** relevant tags as an array — a mix of broad, niche, and local
  (e.g. #BoiseDentist). No banned, spammy, or unrelated tags.

## Examples

- Hook: "Most people brush too hard. Your gums pay for it."
- CTA: "Save this before your next brush. 🪥"
- Hashtags: ["#oralhealth", "#dentaltips", "#flossing", "#boisedentist", "#healthygums", "#preventivecare"]
`,
  },
  {
    id: "sk_personal",
    name: "Personal",
    description: "A human, behind-the-scenes moment — a story, a photo caption, a milestone.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Personal
description: A human, behind-the-scenes moment — a story, a photo caption, a milestone.
category: style
phase: generation
---
A human, behind-the-scenes post — a team moment, a story, a photo caption, a milestone.

## Instructions
- Lead with the real moment or image. Be warm and specific.
- Let personality show; light humor is welcome if it fits the voice.
- Tie back to the brand only if it's natural — never force a sell.

## Example
- "Meet Oliver — our hardest-working team member. Don't let the naps fool you. 🐾"
`,
  },
  {
    id: "sk_story",
    name: "Story",
    description: "A short narrative with a beginning, a turning point, and a result.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Story
description: A short narrative with a beginning, a turning point, and a result.
category: style
phase: generation
---
Tell a short, true story: a moment, what changed, and how it turned out.

## Instructions
1. Open on a specific, relatable moment (the "before").
2. Show the turning point — what happened or what was done.
3. Land the result in concrete terms (time, money, relief, before/after).
4. Make the customer/subject the hero; the brand is the guide.
5. Use ONLY real details from the prompt or sources — never invent a person or a result.

## Example
- "Maria put off the leak for months. By the time she called, it had reached the subfloor. Two days later her kitchen was dry — for a fraction of a full remodel."
`,
  },
  {
    id: "sk_how_to",
    name: "How-to",
    description: "Teach the reader to do one thing, step by step (also good for explainers).",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: How-to
description: Teach the reader to do one thing, step by step (also good for explainers).
category: style
phase: generation
---
Teach the reader to do (or understand) one thing, step by step.

## Instructions
- Start with the outcome they'll get, or the misconception you're clearing up.
- 3–6 concrete, ordered steps (or points). No fluff between them.
- End with the payoff, a rule of thumb, or a "now you can…".
`,
  },
  {
    id: "sk_listicle",
    name: "List",
    description: "A short numbered list of tips, signs, examples, or reasons.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: List
description: A short numbered list of tips, signs, examples, or reasons.
category: style
phase: generation
---
A short numbered list of tips, examples, signs, or reasons.

## Instructions
- A clear premise ("3 ways to…", "5 signs you…").
- Each item: a punchy label + one line of substance.
- Keep it skimmable; cut anything that doesn't add a distinct point.
`,
  },
  {
    id: "sk_faq_post",
    name: "FAQ",
    description: "Answer a real question customers ask, openly and plainly.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: FAQ
description: Answer a real question customers ask, openly and plainly.
category: style
phase: generation
---
Answer a question your audience actually asks ("how much does X cost?", "is it worth it?", "how long does it take?").

## Instructions
1. Put the question in the customer's words as the opener.
2. Give a straight, honest answer first — ranges or "it depends, here's how" are fine.
3. Add the one nuance that helps them decide well.
4. Don't invent prices, timelines, or policies — keep ranges general unless the prompt provides specifics.

## Example
- "\\"How much does a new roof cost?\\" Honest answer: most homes run $8k–$18k depending on size and material. The trap is going cheap on the flashing."
`,
  },
  {
    id: "sk_announcement",
    name: "Announcement",
    description: "Share news — a launch, a milestone, an event, an update.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Announcement
description: Share news — a launch, a milestone, an event, an update.
category: style
phase: generation
---
Share news: a launch, a milestone, an event, an update.

## Instructions
- Lead with the news in the first line. What + why it matters to the reader.
- One or two supporting details. No burying the headline.
- Optional next step, only if there genuinely is one.
`,
  },
  {
    id: "sk_pain_fix",
    name: "Problem → Solution",
    description: "Name a specific problem the reader feels, then show the fix.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Problem → Solution
description: Name a specific problem the reader feels, then show the fix.
category: style
phase: generation
---
Name a specific problem the reader feels, then show the fix.

## Instructions
- Open on a concrete, relatable problem — a real moment, not an abstraction.
- Briefly show the cost of leaving it unsolved.
- Present the fix plainly and the result it produces. Stay honest; no fear-mongering.
`,
  },
  {
    id: "sk_blog_howto",
    name: "How-to / Guide",
    description: "A step-by-step guide that walks the reader to a concrete outcome.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: How-to / Guide
description: A step-by-step guide that walks the reader to a concrete outcome.
category: style
phase: generation
---
A practical, step-by-step long-form guide.

## Instructions
- Open by naming the outcome the reader will achieve and who it's for.
- Use clear <h2> sections, ideally one per step or stage, in logical order.
- Make steps actionable and specific; include tips, gotchas, and examples.
- Close with a short recap and a sensible next step.
`,
  },
  {
    id: "sk_blog_listicle",
    name: "Listicle",
    description: "A numbered list article — scannable, each item earns its place.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Listicle
description: A numbered list article — scannable, each item earns its place.
category: style
phase: generation
---
A numbered long-form list article.

## Instructions
- Lead with a short intro framing why the list matters.
- Each item is its own <h2> (e.g. "1. ..."); a few tight paragraphs each.
- Keep items parallel in structure and genuinely distinct — no filler.
- End with a brief takeaway that ties the list together.
`,
  },
  {
    id: "sk_blog_deepdive",
    name: "Deep-dive / Pillar",
    description: "A thorough, authoritative pillar piece on one topic.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Deep-dive / Pillar
description: A thorough, authoritative pillar piece on one topic.
category: style
phase: generation
---
A comprehensive, authoritative deep-dive (pillar) article.

## Instructions
- Establish scope and why the topic matters up front.
- Organize into substantive <h2>/<h3> sections that build understanding.
- Go beyond surface level: explain mechanisms, trade-offs, and nuance.
- Stay grounded in the provided sources; don't pad. Close with a synthesis.
`,
  },
  {
    id: "sk_blog_casestudy",
    name: "Case study",
    description: "A story of a real problem, the approach, and measurable results.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Case study
description: A story of a real problem, the approach, and measurable results.
category: style
phase: generation
---
A narrative case-study article.

## Instructions
- Set the scene: the subject, their situation, and the problem.
- Walk through the approach taken and why, then what happened.
- Highlight concrete results/outcomes (only ones supported by the input).
- End with the lesson and how it generalizes to the reader.
`,
  },
  {
    id: "sk_blog_opinion",
    name: "Opinion / POV",
    description: "A clear point of view, argued with conviction and fairness.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Opinion / POV
description: A clear point of view, argued with conviction and fairness.
category: style
phase: generation
---
A persuasive opinion / point-of-view essay.

## Instructions
- State the thesis plainly and early.
- Build the argument in ordered <h2> sections with reasons and evidence.
- Fairly acknowledge the strongest counterpoint, then answer it.
- Close with a confident restatement and what it means for the reader.
`,
  },
  {
    id: "sk_blog_comparison",
    name: "Comparison / vs",
    description: "An even-handed comparison that helps the reader choose.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Comparison / vs
description: An even-handed comparison that helps the reader choose.
category: style
phase: generation
---
A structured comparison article (X vs Y, or options A/B/C).

## Instructions
- Frame what's being compared and the decision the reader faces.
- Compare across consistent criteria (use sections or a clear structure).
- Be fair to each option; note who each is best for.
- End with a clear recommendation matched to reader scenarios.
`,
  },
  {
    id: "sk_blog_news",
    name: "Announcement / Update",
    description: "A clear, newsy announcement or product/company update.",
    phase: "generation",
    category: "style",
    enabled: true,
    markdown: `---
name: Announcement / Update
description: A clear, newsy announcement or product/company update.
category: style
phase: generation
---
A long-form announcement or update article.

## Instructions
- Lead with the news itself: what changed and why it matters, in the first lines.
- Give the context and the details readers need (what, when, who it affects).
- Be concrete; avoid hype. Quote or specify only what the input supports.
- Close with what to do next (try it, learn more, get in touch).
`,
  },
  {
    id: "sk_critique",
    name: "Critique",
    description: "Tighten the draft — clarity, voice, and verified claims (no forced CTA).",
    phase: "critique",
    category: "critique",
    enabled: true,
    markdown: `---
name: Critique
description: Tighten the draft — clarity, voice, and verified claims (no forced CTA).
phase: critique
---

## When to use

After generation, as a second pass on every post.

## Instructions

1. **Cut hedges**: "I think", "maybe", "it could be argued" — remove unless load-bearing.
2. **Verify claims**: every number, price, or promise must trace to the prompt/sources, or be removed. Never invent specifics.
3. **Voice check**: does it sound like a real person from this brand? If it reads like a brochure or generic AI, fix it.
4. **Tighten**: cut any sentence that doesn't earn its place; keep it scannable.
5. **Respect the post's intent**: do NOT add a sales CTA to a post that isn't selling. Only sharpen a CTA that's already there or that the post's goal explicitly calls for.

## Output format

Return the revised piece — shorter, sharper, same topic and same intent.

## Examples

> Cut 2 hedge phrases. Removed an unverifiable stat. Replaced "elevate" with "fix". Left the personal post CTA-free.
`,
  },
  {
    id: "sk_compliance_check",
    name: "ComplianceCheck",
    description: "Flag risky or unverifiable claims for regulated industries (health, legal, finance).",
    phase: "critique",
    category: "critique",
    enabled: false,
    markdown: `---
name: ComplianceCheck
description: Flag risky or unverifiable claims for regulated industries (health, legal, finance).
phase: critique
---

## When to use

Enable for regulated or sensitive fields: healthcare, dental, therapy, legal, financial, supplements,
or anything making outcome/results claims. Off by default.

## Instructions

1. Flag and soften **guarantees of outcome** ("cures", "guaranteed results", "pain-free") into honest language.
2. Remove **specific medical/legal/financial claims** not backed by the provided sources.
3. Avoid **superlatives** that invite disputes ("best", "#1", "only") unless provably true and provided.
4. Keep testimonials honest; don't imply typical results from one story.
5. When in doubt, choose **accurate and general** over specific and risky.

## Output format

Return the revised piece plus a one-line note of what was softened or removed and why.

## Examples

> Softened "cures back pain" → "many patients find lasting relief". Removed unbacked "FDA-approved" claim.
`,
  },
  {
    id: "sk_industry_local_service",
    name: "LocalServiceKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: LocalServiceKnowledge
category: industry
phase: generation
---
- Buyers decide on trust and reliability over price — the question on their mind is "will they actually show up and do it right?"
- Real objection: "I've been burned before — no-shows, surprise bills, sloppy work." Defuse with specifics and a no-surprises promise.
- Proof that builds trust: years in the area, the neighborhoods/town served, licensed & insured, guarantees, same-day/after-hours availability, a real review quote.
- Angles that work: a common problem explained plainly, "is it worth fixing or replacing?", seasonal prep, a quick check before you call, a behind-the-scenes job.
- Language: talk like a neighbor, not a brochure. Name the town/area. Concrete over adjectives; cost/time ranges only when the operator provides them.
- Triggers: weather, seasons, holidays, an emergency moment — tie content to it only when genuinely true.
- Avoid: fear-mongering, "best in town" puffery, invented prices or stats.
`,
  },
  {
    id: "sk_industry_dental",
    name: "DentalKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: DentalKnowledge
category: industry
phase: generation
---
- Buyers decide on comfort, gentleness, trust, and convenience more than on clinical detail; nervousness about the dentist is common, so reassurance matters.
- Real objection: "the dentist is expensive, painful, or judgy." Defuse with warmth, painless/gentle framing, insurance accepted, and easy scheduling.
- Proof that builds trust: new-patient specials, insurance/financing accepted, same-day emergencies, kid-friendly, gentle-care language, real patient sentiment.
- Angles that work: answer a real question (cost ranges, "does it hurt?", how often), whitening/Invisalign before-afters, kids' first visit, emergency tips, myth-busting.
- Language: reassuring and plain; define any clinical term. Lead with how the patient will feel, not the procedure.
- Triggers: back-to-school, New Year ("new smile"), wedding/event season, "use-it-or-lose-it" benefits before year-end.
- Avoid: promising medical outcomes or cures, scare tactics, "#1 dentist" claims. Describe care and experience, not guarantees.
`,
  },
  {
    id: "sk_industry_health_clinic",
    name: "HealthClinicKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: HealthClinicKnowledge
category: industry
phase: generation
---
- Buyers decide on hope grounded in evidence — getting function, sleep, mobility, or peace of mind back, framed around the goal rather than the modality.
- Real objection: "will this actually help me, or is it a money pit?" Defuse with realistic expectations and the path to care, not hype.
- Proof that builds trust: credentials, a clear process, focus on the patient's own goal, typical (not guaranteed) experiences, gentle and professional tone.
- Angles that work: frame around the goal (back to work, play with the kids, sleep through the night), at-home tips, "what most people get wrong", when to seek care.
- Language: empathetic and clear; explain the "why" simply. Speak to the outcome the patient wants, not the modality's name.
- Triggers: New-Year health resets, injury seasons (sports, shoveling, gardening), stress periods, post-surgery recovery windows.
- Avoid: diagnosing, guarantees, and absolute claims ("cures", "fixes"). Keep it accurate and reassuring; defer specifics to a real consult.
`,
  },
  {
    id: "sk_industry_trades",
    name: "TradesKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: TradesKnowledge
category: industry
phase: generation
---
- Buyers decide under stress, on trust + urgency; they fear surprise bills, being upsold, and no-shows.
- Real objection: "the last guy ripped me off or didn't fix it right." Counter with transparency — what's wrong, what you'd do, and the range it runs.
- Proof that builds trust: years in the area, licensed & insured, upfront/flat-rate pricing, warranties, maintenance plans, same-day & after-hours, a real review quote.
- Angles that work: seasonal failures (frozen pipes, AC on the hottest day, furnace before winter), "repair vs replace?", quick checks before you call, what a noise/smell means.
- Language: talk like a tech, not a brochure — "here's what's actually happening." Cost/time ranges only when the operator provides them.
- Triggers: weather swings, hosting holidays, home age, the breakdown moment.
- Avoid: fear-mongering, "best in town" puffery, made-up prices or percentages.
`,
  },
  {
    id: "sk_industry_restaurant",
    name: "RestaurantKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: RestaurantKnowledge
category: industry
phase: generation
---
- Buyers decide emotionally and visually — they're choosing the taste, the freshness, and the moment, not a feature list.
- Real objection: "is it worth leaving the house / the price?" Defuse with crave-worthy specifics and an easy next step (book, order, walk in).
- Proof that builds trust: signature dishes, sourcing/made-fresh details, packed-house or regulars energy, real review quotes, the people behind the food.
- Angles that work: a special or new item, behind-the-scenes prep, an event or day-part push (brunch, happy hour, weekend), a staff pick, a seasonal menu.
- Language: casual, sensory, mouth-watering — describe taste, texture, smell. Specific dish names beat "delicious food, great service".
- Triggers: day-part (brunch/lunch/late-night), paydays, weekends, holidays, weather (patio days, comfort food when it's cold), local events.
- Avoid: generic praise, stock-photo vibes, over-claiming ("best food in the city"). Show the food, don't just assert it.
`,
  },
  {
    id: "sk_industry_salon_spa",
    name: "SalonSpaKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: SalonSpaKnowledge
category: industry
phase: generation
---
- Buyers decide on confidence, self-care, and transformation — how they'll look and feel, not the technical service.
- Real objection: "will they get my look right, and is it worth the price?" Defuse with stylist expertise, before/after proof, and effortless booking.
- Proof that builds trust: before/after transformations, stylist skill and specialties, rebooking and referral perks, a real client quote, a clean/welcoming vibe.
- Angles that work: seasonal looks, a new service, a maintenance/rebooking reminder, "how to make it last", a behind-the-chair moment, a client glow-up.
- Language: stylish, warm, and confidence-boosting. Make the next step easy ("book your spot").
- Triggers: seasons and holidays, weddings/events, back-to-school, "new year new look", lapsed-client win-backs.
- Avoid: body-shaming or insecurity-baiting, over-promising results, generic "pampering" filler. Celebrate the client.
`,
  },
  {
    id: "sk_industry_fitness",
    name: "FitnessKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: FitnessKnowledge
category: industry
phase: generation
---
- Buyers decide on feeling strong, healthy, and like they belong; the sale is consistency, accountability, and community — not just workouts.
- Real objection: "I'll be judged, I won't stick with it, or it won't work." Defuse with welcome-everyone energy, easy starts (free trial), and real member wins.
- Proof that builds trust: member transformations and quotes, coaches' expertise, the community vibe, class variety, a low-friction first step (trial, intro week).
- Angles that work: a member win, a form/technique tip, the class schedule, a free-trial offer, mindset/consistency, "start where you are".
- Language: energetic and encouraging; tough-love is fine, shaming is not. Speak to identity and belonging, not just calories.
- Triggers: New Year, post-summer, pre-summer "get ready", January and September signups, challenge launches.
- Avoid: extreme or quick-fix claims ("shredded in 2 weeks", "guaranteed results"), shame, and unrealistic before/afters.
`,
  },
  {
    id: "sk_industry_professional",
    name: "ProfessionalServicesKnowledge",
    description: "",
    phase: "generation",
    category: "industry",
    enabled: true,
    markdown: `---
name: ProfessionalServicesKnowledge
category: industry
phase: generation
---
- Buyers want credibility and clarity, and they're anxious about getting it wrong or overpaying.
- Real objection: "this is confusing, expensive, and I'm not sure I can trust them." Defuse with plain-language explanations and a transparent process.
- Proof that builds trust: clear process and what to expect, credentials, "we handle X so you don't worry about Y", a real client outcome (qualitative), responsiveness.
- Angles that work: answer a common question, deadline reminders (tax season, filing, compliance), "what most people get wrong", a simple framework, demystify a scary topic.
- Language: credible and jargon-free — explain like a smart friend would. Calm and precise; reduce anxiety.
- Triggers: tax season, fiscal year-end, regulatory deadlines, life/business events (starting up, buying, hiring, an audit).
- Avoid: guarantees about outcomes or returns, fear-mongering, and dense jargon. Be helpful and accurate, not salesy.
`,
  },
];
